(() => {
  if (customElements.get('goose-cart-delivery')) return;
  const cache = new Map();
  const parseList = value => {
    if (Array.isArray(value)) return value;
    try { return JSON.parse(String(value).replace(/'/g, '"')); } catch { return []; }
  };
  const assignedObject = (html, key) => {
    const marker = 'Object.assign(window.ns_edd.' + key + ',';
    const offset = html.indexOf(marker);
    if (offset < 0) return {};
    if (!/^\s*\{/.test(html.slice(offset + marker.length))) return {};
    const start = html.indexOf('{', offset + marker.length);
    let depth = 0, quoted = false, escaped = false;
    for (let index = start; index < html.length; index++) {
      const character = html[index];
      if (quoted) {
        if (escaped) escaped = false;
        else if (character === '\\') escaped = true;
        else if (character === '"') quoted = false;
      } else if (character === '"') quoted = true;
      else if (character === '{') depth++;
      else if (character === '}' && --depth === 0) return JSON.parse(html.slice(start, index + 1));
    }
    return {};
  };
  const productSettings = async item => {
    const native = window.ns_edd;
    if (String(native?.product_data?.id) === String(item.id) && native?.app_settings?.global_rules) {
      return { app: native.app_settings, product: native.product_settings, current: native.current_rule?.rule };
    }
    const url = new URL(item.url, location.origin);
    if (url.origin !== location.origin || !url.pathname.includes('/products/')) throw Error('Invalid product URL');
    url.searchParams.set('variant', item.variant);
    const key = url.href;
    const previous = cache.get(key);
    if (previous && Date.now() - previous.time < 300000) return previous.value;
    const value = fetch(key, { credentials: 'same-origin', signal: AbortSignal.timeout(10000) }).then(async response => {
      if (!response.ok) throw Error('Delivery configuration unavailable');
      const html = await response.text();
      const app = assignedObject(html, 'app_settings');
      if (!app.global_rules) throw Error('Delivery configuration unavailable');
      return { app, product: assignedObject(html, 'product_settings') };
    });
    cache.set(key, { time: Date.now(), value });
    value.catch(() => cache.delete(key));
    return value;
  };
  const resolveRule = (item, context) => {
    if (context.current?.estimated_delivery) return context.current;
    const { app, product } = context;
    const flags = app.app_settings_config || {};
    if (flags.is_active_app === false) return null;
    if (flags.is_product_rules !== false && product?.estimated_delivery) return product;
    const rules = flags.is_collection_rules === false ? [] : [...(app.collections_rules || [])].sort((a, b) => Number(b.date) - Number(a.date));
    const edd = rules.find(rule => String(rule.type).toUpperCase() === 'EDD' && parseList(rule.product_ids).map(String).includes(String(item.id)));
    const collection = rules.find(rule => String(rule.type).toUpperCase() === 'SHOPIFY' && item.collections.includes(String(rule.id).replace('gid://shopify/Collection/', '')));
    return edd || collection || (flags.is_global_rules !== false ? app.global_rules : null);
  };
  const addDay = date => { const next = new Date(date); next.setDate(next.getDate() + 1); return next; };
  const workingDate = (date, count, days, holidays) => {
    const allowed = new Set(parseList(days));
    if (!allowed.size || !Number.isInteger(count) || count < 0 || count > 366) throw Error('Invalid delivery schedule');
    const names = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const valid = value => allowed.has(names[value.getDay()]) && !holidays.has(value.getDate() + '/' + value.getMonth());
    let result = new Date(date), iterations = 0;
    while (!valid(result) && iterations++ < 500) result = addDay(result);
    if (!valid(result)) throw Error('Invalid delivery schedule');
    for (let elapsed = 0; elapsed < count && iterations++ < 1500;) {
      result = addDay(result);
      if (valid(result)) elapsed++;
    }
    if (iterations >= 1500) throw Error('Invalid delivery schedule');
    return result;
  };
  const deliveryWindow = (rule, app, country) => {
    if (!rule?.estimated_delivery) throw Error('Delivery rule unavailable');
    const estimate = rule.estimated_delivery, widget = rule.widget_settings || {};
    if (widget.display_option && widget.display_option !== 'default') throw Error('Destination details required');
    const ready = parseList(estimate.readyRange), delivery = parseList(estimate.deliRange);
    const holidays = new Set((estimate.isHolidays ? estimate.dataHolidaysTable || [] : []).map(value => {
      const date = new Date(Number(value.date) * 1000);
      return date.getDate() + '/' + date.getMonth();
    }));
    const now = new Date();
    let today = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12);
    if (estimate.isCutOffTime && estimate.cotTime) {
      const cutoff = new Date(Number(estimate.cotTime) * 1000);
      const parts = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23', ...(widget.isAutoTimezone ? {} : { timeZone: widget.timezone }) }).formatToParts(now);
      const current = Number(parts.find(part => part.type === 'hour').value) * 60 + Number(parts.find(part => part.type === 'minute').value);
      if (current >= cutoff.getHours() * 60 + cutoff.getMinutes()) today = addDay(today);
    }
    const preparation = workingDate(today, Number(ready[1]), estimate.readyDay, holidays);
    const origin = app.app_settings_config?.delivery_date_calculation === 'order_date' ? today : preparation;
    const destination = rule === app.global_rules ? app.delivery_country?.[country.toLowerCase()] : null;
    return {
      preparation,
      min: workingDate(origin, Number(destination?.min ?? delivery[0]), estimate.deliDay, holidays),
      max: workingDate(origin, Number(destination?.max ?? delivery[1]), estimate.deliDay, holidays)
    };
  };
  class GooseCartDelivery extends HTMLElement {
    async connectedCallback() {
      if (!this.cartUpdateUnsubscriber && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, ({ cartData }) => {
          const footer = document.getElementById('main-cart-footer');
          const html = cartData?.sections?.[footer?.dataset.id] || cartData?.sections?.['cart-drawer'];
          if (!html) return;
          // Keep the shipping benefit in sync with Shopify's rendered total, including discounts.
          const updated = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-delivery-shipping]');
          const current = this.querySelector('[data-delivery-shipping]');
          if (current && updated) current.replaceWith(updated);
        });
      }
      const generation = this.gmsGeneration = (this.gmsGeneration || 0) + 1;
      this.dataset.orderPromises = "";
      const source = this.querySelector('[data-delivery-config]');
      if (!source) return;
      const config = JSON.parse(source.textContent);
      const items = config.items.filter(item => item.shipping);
      if (!items.length) { this.hidden = true; return; }
      const label = this.querySelector('[data-delivery-estimate]');
      const link = this.querySelector('a');
      const fallback = () => {
        label.textContent = config.estimated;
        link.textContent = config.unavailable;
        this.dataset.state = 'unavailable';
      };
      if (this.dataset.country !== 'MX') { fallback(); return; }
      try {
        const unique = [...new Map(items.map(item => [item.variant, item])).values()];
        const windows = await Promise.all(unique.map(async item => {
          const context = await productSettings(item);
          return { variant: String(item.variant), ...deliveryWindow(resolveRule(item, context), context.app, this.dataset.country) };
        }));
        if (!this.isConnected || generation !== this.gmsGeneration) return;
        const minimum = new Date(Math.max(...windows.map(window => window.min.getTime())));
        const maximum = new Date(Math.max(...windows.map(window => window.max.getTime())));
        const format = date => new Intl.DateTimeFormat(config.dateLocale || this.dataset.locale || 'es', { day: 'numeric', month: 'short', ...(date.getFullYear() === new Date().getFullYear() ? {} : { year: 'numeric' }) }).format(date);
        const dates = minimum.getTime() === maximum.getTime() ? config.on + ' ' + format(minimum) : config.between + ' ' + format(minimum) + ' ' + config.and + ' ' + format(maximum);
        label.textContent = config.today + ' ' + dates;
        this.dataset.state = 'ready';
        this.dataset.minimum = minimum.toISOString();
        this.dataset.maximum = maximum.toISOString();
        const fullDate = date => [String(date.getDate()).padStart(2, '0'), String(date.getMonth() + 1).padStart(2, '0'), date.getFullYear()].join('/');
        this.dataset.orderPromises = JSON.stringify(Object.fromEntries(windows.map(value => [value.variant, {
          _ns_edd_app: 'true', _ns_edd_message_title: 'Entrega prometida en México',
          'Entrega prometida en México': 'Entre ' + fullDate(value.min) + ' y ' + fullDate(value.max) + '. Listo para envío: ' + fullDate(value.preparation)
        }])));
      } catch { if (this.isConnected && generation === this.gmsGeneration) fallback(); }
    }

    disconnectedCallback() {
      this.cartUpdateUnsubscriber?.();
      this.cartUpdateUnsubscriber = undefined;
    }
  }
  GooseCartDelivery.captureForItems = async function(items, country) {
    if (country !== 'MX') return {};
    const windows = await Promise.all(items.filter(item => item.shipping).map(async item => {
      const context = await productSettings(item);
      return { variant: String(item.variant), ...deliveryWindow(resolveRule(item, context), context.app, country) };
    }));
    const fullDate = date => [String(date.getDate()).padStart(2, '0'), String(date.getMonth() + 1).padStart(2, '0'), date.getFullYear()].join('/');
    return Object.fromEntries(windows.map(value => [value.variant, {
      _ns_edd_app: 'true', _ns_edd_message_title: 'Entrega prometida en México',
      'Entrega prometida en México': 'Entre ' + fullDate(value.min) + ' y ' + fullDate(value.max) + '. Listo para envío: ' + fullDate(value.preparation)
    }]));
  };
  GooseCartDelivery.prototype.captureProperties = async function() {
    await this.connectedCallback();
    if (this.dataset.country !== 'MX') return {};
    if (this.dataset.state !== 'ready' || !this.dataset.orderPromises) throw Error('No se pudo confirmar la fecha de entrega. Revisa tu selección o solicita ayuda antes de pagar.');
    return JSON.parse(this.dataset.orderPromises);
  };
  class GooseCartTicker extends HTMLElement {
    connectedCallback() {
      const toggle = this.querySelector('button');
      if (!toggle || this.bound) return;
      this.bound = true;
      toggle.addEventListener('click', () => {
        const paused = this.dataset.paused !== 'true';
        this.dataset.paused = String(paused);
        toggle.setAttribute('aria-pressed', String(paused));
      });
    }
  }
  customElements.define('goose-cart-delivery', GooseCartDelivery);
  customElements.define('goose-cart-ticker', GooseCartTicker);
})();

